/**
 * Rebel Crown / Rebel Lux Crown / Rebel Bridge cart conflict guard
 * -------------------------------------------------------------------------
 * Some of these product families can't share a cart (they fulfil from
 * different locations); the clashing pairs are listed in CONFLICTS. Each
 * parent is added by GPO along with its arch products, so a "family" is the
 * parent plus its arches. If a clashing pair ends up in the cart, this shows
 * a popup: Substitute (keep the family just added) or Cancel (keep what was
 * already there). Either choice removes the losing family or families
 * entirely (parent + arches) and reloads.
 *
 * Two triggers, so it works regardless of cart type:
 *   1. Cart-page load  -> the reliable path for cart type = "page", where
 *      adding does a native form POST and lands the customer on /cart.
 *   2. Add interception -> covers drawer/notification carts and GPO's own
 *      AJAX adds, where no navigation happens.
 *
 * Detection and cleanup key on product IDs only (always present in /cart.js),
 * so there's no dependency on GPO's private group properties.
 *
 * Drop this in assets/ and load it in theme.liquid before </body>:
 *   <script src="{{ 'rebel-crown-conflict.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  'use strict';

  // --- Config --------------------------------------------------------------
  // Each family = parent + every arch product it can pull in.
  // Use PRODUCT IDs here, not variant IDs.
  // Which families clash is set in CONFLICTS below.
  var FAMILIES = [
    {
      name: 'Rebel Crown',
      ids: [
        9702251036960, // Rebel Crown (parent)
        9702272106784, // Upper Arch
        9702261817632  // Lower Arch
      ]
    },
    {
      name: 'Rebel Lux Crown',
      ids: [
        10128697753888, // Rebel Lux Crown (parent)
        10128705421600, // Lux Upper Arch
        10128708305184  // Lux Lower Arch
      ]
    },
    {
      name: 'Rebel Bridge',
      ids: [
        15402894754080, // Rebel Bridge (parent)
        15402909434144, // Bridge Upper Arch
        15402913268000  // Bridge Lower Arch
      ]
    }
  ];
  // Pairs of families that can NOT share a cart (by family name).
  // Any pair not listed here is allowed together.
  var CONFLICTS = [
    ['Rebel Crown', 'Rebel Lux Crown'],
    ['Rebel Bridge', 'Rebel Lux Crown']
  ];
  var EVALUATE_DELAY = 350; // ms; let GPO finish adding its grouped items

  // --- State ---------------------------------------------------------------
  var resolving = false;      // popup open / removal in progress
  var lastAddedFamily = null; // index of the family most recently added (if intercepted)
  var evalTimer = null;
  var origFetch = window.fetch;

  function familyOf(productId) {
    productId = Number(productId);
    for (var i = 0; i < FAMILIES.length; i++) {
      if (FAMILIES[i].ids.indexOf(productId) !== -1) return i;
    }
    return -1;
  }

  function conflictsWith(a, b) {
    if (a === b) return false;
    var na = FAMILIES[a].name, nb = FAMILIES[b].name;
    return CONFLICTS.some(function (pair) {
      return (pair[0] === na && pair[1] === nb) || (pair[0] === nb && pair[1] === na);
    });
  }

  // --- Trigger 1: cart-page load ------------------------------------------
  function init() {
    var cartPath = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    var here = window.location.pathname.replace(/\/+$/, '');
    if (here === cartPath || here.endsWith(cartPath)) {
      scheduleEvaluate();
    }
  }

  // --- Trigger 2: detect adds (fetch + XHR) --------------------------------
  function captureAdded(data) {
    if (!data) return;
    var items = data.items || [data];
    items.forEach(function (it) {
      if (!it) return;
      var fi = familyOf(it.product_id);
      if (fi !== -1) lastAddedFamily = fi;
    });
  }

  window.fetch = function () {
    var url = (arguments[0] && arguments[0].url) || arguments[0];
    var p = origFetch.apply(this, arguments);
    if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
      p.then(function (res) {
        try { res.clone().json().then(captureAdded).catch(function () {}); } catch (e) {}
        scheduleEvaluate();
      });
    }
    return p;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCartAdd = typeof url === 'string' && url.indexOf('/cart/add') !== -1;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__isCartAdd) {
      this.addEventListener('load', function () {
        try { captureAdded(JSON.parse(this.responseText)); } catch (e) {}
        scheduleEvaluate();
      });
    }
    return origSend.apply(this, arguments);
  };

  function scheduleEvaluate() {
    clearTimeout(evalTimer);
    evalTimer = setTimeout(evaluate, EVALUATE_DELAY);
  }

  // --- Conflict check ------------------------------------------------------
  function evaluate() {
    if (resolving) return;
    origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Which families are in the cart right now?
        var presentIdx = [];
        FAMILIES.forEach(function (f, i) {
          if (cart.items.some(function (it) { return familyOf(it.product_id) === i; })) {
            presentIdx.push(i);
          }
        });
        if (presentIdx.length < 2) return; // need at least two families to conflict

        function clashesFor(idx) {
          return presentIdx.filter(function (j) { return conflictsWith(idx, j); });
        }

        // Which family was just added? Use the intercepted value if we have
        // it, otherwise fall back to the first family line item in the cart
        // (Shopify's /cart.js lists the most recently added lines first).
        var incoming = lastAddedFamily;
        if (incoming == null || presentIdx.indexOf(incoming) === -1) {
          incoming = presentIdx[0];
          for (var i = 0; i < cart.items.length; i++) {
            var fi = familyOf(cart.items[i].product_id);
            if (fi !== -1) { incoming = fi; break; }
          }
        }

        var existing = clashesFor(incoming);
        if (!existing.length) {
          // The guessed family clashes with nothing in the cart; check whether
          // any other present family does (e.g. allowed pair plus a clash).
          for (var k = 0; k < presentIdx.length; k++) {
            if (clashesFor(presentIdx[k]).length) {
              incoming = presentIdx[k];
              existing = clashesFor(incoming);
              break;
            }
          }
        }
        if (!existing.length) return; // only allowed combinations present

        resolving = true;
        openPopup(incoming, existing, cart);
      })
      .catch(function () {});
  }

  // --- Remove whole families (parent + all arch line items) ----------------
  function removeFamilies(indexes, cart) {
    var updates = {};
    cart.items.forEach(function (it) {
      if (indexes.indexOf(familyOf(it.product_id)) !== -1) updates[it.key] = 0;
    });
    return origFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }

  function finishWithReload(removePromise) {
    removePromise
      .then(function () { window.location.reload(); })
      .catch(function () { window.location.reload(); });
  }

  // --- Popup (accessible dialog) -------------------------------------------
  function openPopup(incoming, existing, cart) {
    injectStyles();
    var lastFocused = document.activeElement;

    var existingNames = existing.map(function (i) { return FAMILIES[i].name; }).join(' and ');

    var overlay = document.createElement('div');
    overlay.className = 'rebel-conflict__overlay';

    var dialog = document.createElement('div');
    dialog.className = 'rebel-conflict__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'rebel-conflict-title');

    var title = document.createElement('h2');
    title.className = 'rebel-conflict__title';
    title.id = 'rebel-conflict-title';
    title.textContent = "Item can't be combined";

    var msg = document.createElement('p');
    msg.className = 'rebel-conflict__msg';
    msg.textContent = "You can't add " + FAMILIES[incoming].name + ' because you already have ' +
      existingNames + ' in your cart. Would you like to substitute it?';

    var actions = document.createElement('div');
    actions.className = 'rebel-conflict__actions';

    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'rebel-conflict__btn';
    cancelBtn.textContent = 'Cancel';

    var subBtn = document.createElement('button');
    subBtn.type = 'button';
    subBtn.className = 'rebel-conflict__btn rebel-conflict__btn--primary';
    subBtn.textContent = 'Substitute';

    actions.appendChild(cancelBtn);
    actions.appendChild(subBtn);
    dialog.appendChild(title);
    dialog.appendChild(msg);
    dialog.appendChild(actions);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';

    function close() {
      document.documentElement.style.overflow = '';
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    // Substitute: drop every family already in the cart, keep the one just added.
    subBtn.addEventListener('click', function () {
      close();
      finishWithReload(removeFamilies(existing, cart));
    });
    // Cancel: drop the family just added, leave the cart as it was.
    cancelBtn.addEventListener('click', function () {
      close();
      finishWithReload(removeFamilies([incoming], cart));
    });

    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); cancelBtn.click(); return; }
      if (e.key === 'Tab') {
        var f = [cancelBtn, subBtn];
        var idx = f.indexOf(document.activeElement);
        if (e.shiftKey && idx <= 0) { e.preventDefault(); subBtn.focus(); }
        else if (!e.shiftKey && idx === f.length - 1) { e.preventDefault(); cancelBtn.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    subBtn.focus();
  }

  function injectStyles() {
    if (document.getElementById('rebel-conflict-styles')) return;
    var css =
      '.rebel-conflict__overlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.5);}' +
      '.rebel-conflict__dialog{background:#fff;color:#121212;max-width:420px;width:100%;border-radius:8px;padding:24px;box-shadow:0 10px 40px rgba(0,0,0,.25);font-family:inherit;}' +
      '.rebel-conflict__title{margin:0 0 8px;font-size:1.15rem;line-height:1.3;}' +
      '.rebel-conflict__msg{margin:0 0 20px;font-size:.95rem;line-height:1.5;}' +
      '.rebel-conflict__actions{display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;}' +
      '.rebel-conflict__btn{cursor:pointer;border:1px solid currentColor;background:transparent;color:#121212;padding:10px 18px;border-radius:4px;font:inherit;font-size:.9rem;min-height:44px;}' +
      '.rebel-conflict__btn--primary{background:#121212;color:#fff;border-color:#121212;}' +
      '.rebel-conflict__btn:focus-visible{outline:2px solid #005fcc;outline-offset:2px;}';
    var style = document.createElement('style');
    style.id = 'rebel-conflict-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // --- Boot ----------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();